import React, { useState } from 'react';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import TextWithIcon from 'components/TextWithIcon';
import MediaObject from 'components/MediaObject';

import CoachingCoordinatorCoachList from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorCoachList';
import CoachingCoordinatorParticipantList from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorParticipantList';
import CoachingCoordinatorAddCoachSidebar from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorAddCoachSidebar';
import Heading from 'components/Heading';
import TabPanel from 'components/TabPanel';
import Tabs from 'components/Tabs';
import { GetParticipantList_coaching_participants_participants } from 'coaching/queries/__generated__/GetParticipantList';
import { Coaches_coaching_coaches_coaches } from 'coaching/queries/__generated__/Coaches';
import cx from 'classnames';
import Input from 'components/Input';
import { PeopleViewProps } from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorView';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import '../styles/CoachingCoordinatorPeopleListView.scss';
import Row from 'components/Row';

import { useCoachingViewState } from 'coaching/components/CoachingView';

export type ParticipantRow = GetParticipantList_coaching_participants_participants;
export type CoachRow = Coaches_coaching_coaches_coaches;

export type Props = PeopleViewProps & {
  onNameQueryChanged: (name: string) => void;
  onShowScheduleSessionView: () => void;
};

export default function CoachingCoordinatorPeopleListView(props: Props) {
  const [activeTab, setActiveTab] = useState(0);
  const { setSidebar, closeSidebar } = useCoachingViewState();
  const {
    participants,
    loadingParticipants,
    participantsError,
    coaches,
    loadingCoaches,
    coachesError,
    nameQuery,
  } = props;

  let content: JSX.Element;
  if ((!participants && loadingParticipants) || (!coaches && loadingCoaches)) {
    content = <CenteredLoadingIndicator />;
  } else if (participantsError || !participants) {
    return <ErrorReporter error={participantsError ?? 'Could not load coaching participants'} />;
  } else if (coachesError || !coaches) {
    return <ErrorReporter error={coachesError ?? 'Could not load coaching coaches'} />;
  } else {
    content = (
      <PeopleListContent
        {...props}
        setActiveTab={(activeTab: number) => {
          setActiveTab(activeTab);
          closeSidebar();
        }}
        activeTab={activeTab}
      />
    );
  }

  const openCreateNewCoachSidebar = () => {
    setSidebar(<CoachingCoordinatorAddCoachSidebar onHide={closeSidebar} nameQuery={nameQuery} />);
  };

  return (
    <BoxBody>
      <MediaObject figure="right">
        <Heading level="1" serif>
          People
        </Heading>
        {activeTab === 1 && (
          <Button
            level="secondary"
            data-test="add-session-button"
            onClick={openCreateNewCoachSidebar}>
            <TextWithIcon icon="+" iconSize="medium" size="xsmall">
              Create new coach
            </TextWithIcon>
          </Button>
        )}
      </MediaObject>
      <PeopleSearchBox nameQuery={props.nameQuery} onNameQueryChanged={props.onNameQueryChanged} />
      {content}
    </BoxBody>
  );
}

type PeopleListContentProps = {
  setActiveTab: (index: number) => void;
  activeTab: number;
  onShowScheduleSessionView: () => void;
};

function PeopleListContent(props: Props & PeopleListContentProps) {
  function onTabChange(tabIndex: number) {
    props.setActiveTab(tabIndex);
  }

  return (
    <Tabs flush={false} onTabChange={onTabChange} tabActive={props.activeTab}>
      <TabPanel title="Students">
        <CoachingCoordinatorParticipantList
          showScheduleSessionView={props.onShowScheduleSessionView}
          participants={props.participants!}
          selectedParticipant={props.selectedParticipant}
          loadingParticipants={props.loadingParticipants}
          onSelectParticipant={props.onSelectParticipant}
          onNextPage={props.participantPagination.onNextPage}
          hasNextPage={props.participantPagination.hasNextPage}
        />
      </TabPanel>
      <TabPanel title="Coaches">
        <CoachingCoordinatorCoachList
          coaches={props.coaches!}
          loadingCoaches={props.loadingCoaches}
          onNextPage={props.coachPagination.onNextPage}
          hasNextPage={props.coachPagination.hasNextPage}
          nameQuery={props.nameQuery}
        />
      </TabPanel>
    </Tabs>
  );
}

function PeopleSearchBox(props: { nameQuery: string; onNameQueryChanged: (name: string) => void }) {
  function onNameChanged(event: React.FormEvent<HTMLInputElement>) {
    const newInput = event.currentTarget.value;
    props.onNameQueryChanged(newInput);
  }

  return (
    <Row className={cx('PeopleSearchBox')}>
      <Input
        glyph="search"
        className="PeopleSearchBox-input"
        onChange={onNameChanged}
        value={props.nameQuery}
        data-test="people-search-input"
      />
    </Row>
  );
}
